import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Auth} from '../actions/auth.actions';
import {ShippingProviderConfiguration} from '../models/shipping/shippingProviderConfiguration.model';
import {ShippingService} from '../services/shipping.service';
import {tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {Shipping} from '../actions/shipping.actions';
import {ShippingOrder} from '../models/shipping/shippingOrder.model';
import ClearState = Auth.ClearState;
import GetProvider = Shipping.GetProvider;
import GetProviders = Shipping.GetProviders;
import CreateProvider = Shipping.CreateProvider;
import UpdateProvider = Shipping.UpdateProvider;
import DeleteProvider = Shipping.DeleteProvider;
import GetShippingProviders = Shipping.GetShippingProviders;
import GetShippingOrders = Shipping.GetShippingOrders;
import GetShippingOrder = Shipping.GetShippingOrder;
import RequestShippingOrder = Shipping.RequestShippingOrder;
import UpdateShippingOrderLocal = Shipping.UpdateShippingOrderLocal;
import UpdateShippingOrderApi = Shipping.UpdateShippingOrderApi;

export interface ShippingStateModel {
  providers: Array<ShippingProviderConfiguration>;
  providerTypes: Array<string>;
  currentShippingOrder: ShippingOrder | null;
  shippingOrders: Array<ShippingOrder>;
  currentProvider: ShippingProviderConfiguration | null;
}

@State<ShippingStateModel>({
  name: 'shipping',
  defaults: {
    providers: [],
    providerTypes: [],
    currentProvider: null,
    shippingOrders: [],
    currentShippingOrder: null,
  }
})
@Injectable()
export class ShippingState {
  constructor(private shippingService: ShippingService) {
  }

  @Selector()
  static providers(state: ShippingStateModel): Array<ShippingProviderConfiguration> {
    return state.providers;
  }

  @Selector()
  static providerTypes(state: ShippingStateModel): Array<string> {
    return state.providerTypes;
  }

  @Selector()
  static provider(state: ShippingStateModel): ShippingProviderConfiguration {
    return state.currentProvider;
  }

  @Selector()
  static shippingOrder(state: ShippingStateModel): ShippingOrder {
    return state.currentShippingOrder;
  }

  @Action(GetShippingOrder)
  getShippingOrder(ctx: StateContext<ShippingStateModel>, action: GetShippingOrder): Observable<ShippingOrder> {
    if (!action.force) {
      const shippingOrder = ctx.getState().currentShippingOrder;
      if (shippingOrder && shippingOrder.id === action.id) {
        return of(shippingOrder);
      }

      const cachedShippingOrder = ctx.getState().shippingOrders.find(so => so.id === action.id);
      if (cachedShippingOrder) {
        ctx.patchState({
          currentShippingOrder: cachedShippingOrder,
        });
        return of(cachedShippingOrder);
      }
    }
    return this.shippingService.getShippingOrder(action.id).pipe(tap(result => {
      ctx.patchState({
        currentShippingOrder: result,
      });
    }));
  }

  @Action(RequestShippingOrder)
  requestShippingOrder(ctx: StateContext<ShippingStateModel>, action: RequestShippingOrder): Observable<ShippingOrder> {
    return this.shippingService.requestShippingOrder(action.id).pipe(tap(result => {
      ctx.patchState({
        currentShippingOrder: result,
      });
    }));
  }

  @Action(GetShippingOrders)
  getShippingOrders(ctx: StateContext<ShippingStateModel>, action: GetShippingOrders): Observable<Array<ShippingOrder>> {
    if (!action.force) {
      const shippingOrders = ctx.getState().shippingOrders;
      if (shippingOrders.length > 0) {
        return of(shippingOrders);
      }
    }
    return this.shippingService.getShippingOrders().pipe(tap(result => {
      ctx.patchState({
        shippingOrders: result,
      });
    }));
  }

  @Action(GetProviders)
  getProviders(ctx: StateContext<ShippingStateModel>, action: GetProviders): Observable<Array<ShippingProviderConfiguration>> {
    if (!action.force) {
      const providers = ctx.getState().providers;
      if (providers.length > 0) {
        return of(providers);
      }
    }
    return this.shippingService.getProviders().pipe(tap(result => {
      ctx.patchState({
        providers: result,
      });
    }));
  }

  @Action(GetProvider)
  getProvider(ctx: StateContext<ShippingStateModel>, action: GetProvider): Observable<ShippingProviderConfiguration> {
    if (!action.force) {
      const provider = ctx.getState().currentProvider;
      if (provider && provider.id === action.id) {
        return of(provider);
      }

      const cachedProvider = ctx.getState().providers.find(provdr => provdr.id === action.id);
      if (cachedProvider) {
        ctx.patchState({
          currentProvider: cachedProvider,
        });
        return of(cachedProvider);
      }
    }
    return this.shippingService.getProvider(action.id).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(CreateProvider)
  createProvider(ctx: StateContext<ShippingStateModel>, action: CreateProvider): Observable<ShippingProviderConfiguration> {
    return this.shippingService.createProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(UpdateProvider)
  updateProvider(ctx: StateContext<ShippingStateModel>, action: UpdateProvider): Observable<ShippingProviderConfiguration> {
    return this.shippingService.updateProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: result,
      });
    }));
  }

  @Action(DeleteProvider)
  deleteProvider(ctx: StateContext<ShippingStateModel>, action: DeleteProvider): Observable<ShippingProviderConfiguration> {
    const providers = ctx.getState().providers;
    const index = providers.indexOf(action.provider);
    return this.shippingService.deleteProvider(action.provider).pipe(tap(result => {
      ctx.patchState({
        currentProvider: null,
        providers: [
          ...providers.slice(0, index),
          ...index === providers.length - 1 ? [] : providers.slice(index + 1)
        ]
      });
    }));
  }

  @Action(GetShippingProviders)
  getShippingProviderTypes(ctx: StateContext<ShippingStateModel>, action: GetShippingProviders): Observable<Array<string>> {
    if (!action.force) {
      const providers = ctx.getState().providerTypes;
      if (providers.length > 0) {
        return of(providers);
      }
    }
    return this.shippingService.getShippingProviders().pipe(tap(result => {
      ctx.patchState({
        providerTypes: result
      });
    }));
  }

  @Action(UpdateShippingOrderLocal)
  updateJobPaymentLocal(ctx: StateContext<ShippingStateModel>, action: UpdateShippingOrderLocal): ShippingOrder {
    ctx.patchState({
      currentShippingOrder: action.shippingOrder
    });
    return action.shippingOrder;
  }

  @Action(UpdateShippingOrderApi)
  updateJobPaymentApi(ctx: StateContext<ShippingStateModel>, action: UpdateShippingOrderApi): Observable<ShippingOrder> {
    return this.shippingService.updateShippingOrder(action.id, action.shippingOrder).pipe(tap(result => {
      ctx.patchState({
        currentShippingOrder: result
      });
    }));
  }

  @Action(ClearState)
  clearState(ctx: StateContext<ShippingStateModel>): void {
    ctx.setState({
      providers: [],
      providerTypes: [],
      currentShippingOrder: null,
      shippingOrders: [],
      currentProvider: null,
    });
  }
}
